import { useDebounceHook } from "@/hooks/use-debounce";
import type {
  CatalogSearchItem,
  CommonInfiniteQueryType,
} from "@/types/common/filters";
import { searchTypesEnum } from "@/types/common/filters";
import type { RolePageData, RoleType } from "@/types/entities/roles";
import type {
  ApiTagsInfiniteResponseType,
  TagType,
} from "@/types/entities/tags";
import type { UserPageData, UserType } from "@/types/entities/users";
import { Icon } from "@elaniintc/goes-design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import type {
  ApiCatalogInfiniteResponseType,
  FilterSearchProps,
  SearchTypeMap,
} from "./FilterSearch.types";
import { getListType } from "./FilterSearch.helpers";
import { DropdownContent } from "./FilterSearch.dropdown";
import { UserCard, RoleCard, TagCard, CatalogCard } from "./FilterSearch.cards";

export default function FilterSearch({
  title,
  initialSearchText,
  searchField,
  searchType,
  selected,
  result,
  useQueryHook,
  onUpdateSearch,
  onUpdateSearchFromResult,
}: Readonly<FilterSearchProps>) {
  const [searchText, setSearchText] = useState(initialSearchText);
  const debouncedSearchText = useDebounceHook(searchText, 500);
  const [isOpen, setIsOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { ref: loaderRef, inView } = useInView();

  const queryParams = useMemo(() => {
    if (debouncedSearchText.length > 3) {
      return {
        [searchField]: debouncedSearchText,
      };
    }
    return {};
  }, [debouncedSearchText, searchField]);

  const typedHook = useQueryHook as CommonInfiniteQueryType<
    SearchTypeMap[typeof searchType]["response"],
    SearchTypeMap[typeof searchType]["filters"]
  >;

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } = typedHook(
    {
      ...queryParams,
      per_page: 80,
      processes: "*",
    },
  );

  useEffect(() => {
    const defaultResults = result?.split(",") || [];
    let newItems: UserType[] | RoleType[] | CatalogSearchItem[] = [];
    if (selected.length === 0 && defaultResults.length > 0) {
      if (!(data && "pages" in data)) return;

      if (searchType === searchTypesEnum.user) {
        const userItems: UserType[] = [];
        data?.pages.forEach((page) => {
          page.data.forEach((item) => {
            if (defaultResults.includes(item.id)) {
              if (item) {
                userItems.push(item as UserType);
              }
            }
          });
        });
        newItems = userItems;
      } else if (searchType === searchTypesEnum.roles) {
        const roleItems: RoleType[] = [];
        data?.pages.forEach((page) => {
          page.data.forEach((item) => {
            if (defaultResults.includes(item.id)) {
              if (item) {
                roleItems.push(item as RoleType);
              }
            }
          });
        });
        newItems = roleItems;
      }
      if (
        searchType === searchTypesEnum.positions ||
        searchType === searchTypesEnum.institutions
      ) {
        const catalogItems: CatalogSearchItem[] = [];
        (data?.pages as ApiCatalogInfiniteResponseType[] | undefined)?.forEach(
          (page) => {
            page.data.forEach((item) => {
              if (defaultResults.includes(item.id) && item) {
                catalogItems.push(item);
              }
            });
          },
        );
        newItems = catalogItems;
      }
    }
    if (newItems.length > 0) {
      onUpdateSearchFromResult?.(newItems);
    }
  }, [data, selected, result, onUpdateSearchFromResult, searchType]);

  const _data = useMemo(() => {
    if (!data) return [];
    if (!("pages" in data)) return [];

    if (searchType === searchTypesEnum.user) {
      const newData = (data.pages as UserPageData[])
        .flatMap((page) => page.data)
        .map((item) => {
          return {
            ...item,
            roles: Array.isArray(item.user_roles) ? item.user_roles : [],
          } as UserType;
        });

      return newData;
    }

    if (searchType === searchTypesEnum.roles) {
      const newData = (data.pages as RolePageData[])
        .flatMap((page) => page.data)
        .filter((field) => {
          if (selected.some((u) => u.id === field.id)) {
            return false;
          }
          return true;
        })
        .map((item) => ({
          ...item,
        }));

      return newData;
    }

    if (searchType === searchTypesEnum.tags) {
      const newData = (data.pages as ApiTagsInfiniteResponseType["pages"])
        .flatMap((page) => page.data)
        .map((item) => ({
          ...item,
        }));
      return newData;
    }

    if (
      searchType === searchTypesEnum.positions ||
      searchType === searchTypesEnum.institutions
    ) {
      const newData = (data.pages as ApiCatalogInfiniteResponseType[])
        .flatMap((page) => page.data)
        .filter((field) => {
          if (selected.some((u) => u.id === field.id)) {
            return false;
          }
          return true;
        })
        .map((item) => ({
          id: item.id,
          name: item.name || item.nombre || "-",
          nombre: item.nombre,
          description: item.description,
          activo: item.activo,
        }));
      return newData;
    }

    return [];
  }, [data, selected, searchType]);

  const handleSelectUser = (
    user: UserType,
    action: "add" | "remove" = "add",
  ) => {
    onUpdateSearch(action, user);
    setIsOpen(false);
  };

  useEffect(() => {
    if (inView && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetching, fetchNextPage]);

  useEffect(() => {
    if (
      isOpen &&
      _data.length === 0 &&
      !isLoading &&
      !isFetching &&
      debouncedSearchText.length > 3
    ) {
      fetchNextPage();
    }
  }, [
    isOpen,
    _data.length,
    isLoading,
    isFetching,
    debouncedSearchText,
    fetchNextPage,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        event.target instanceof Node &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex flex-col space-y-6 p-6">
      <div className="flex justify-between">
        <h5 className="text-heading text-xl font-semibold">{title}</h5>
      </div>
      <div className="flex flex-col space-y-0.5">
        <div className="flex space-x-2.5">
          <div className="h-15 relative w-full tablet:min-w-89">
            <div className="relative">
              <div className="relative bg-neutral-object-accent-silent border border-neutral rounded-lg py-1.5 px-3">
                <Icon
                  type="far"
                  name="search"
                  className="absolute left-2.5 top-1/2 transform -translate-y-1/2 font-normal text-body text-base leading-3xl"
                />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder={`Buscar ${(() => {
                    switch (searchType) {
                      case searchTypesEnum.user:
                        return "usuarios";
                      case searchTypesEnum.roles:
                        return "roles";
                      case searchTypesEnum.tags:
                        return "etiquetas";
                      case searchTypesEnum.positions:
                        return "cargos";
                      case searchTypesEnum.institutions:
                        return "instituciones";
                      default:
                        return "unknown";
                    }
                  })()}...`}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={() => setIsOpen(true)}
                  className="w-full pl-8.5 text-lg font-normal bg-transparent border-none outline-none focus:ring-0 text-body placeholder:text-body-quiet"
                />
              </div>
            </div>

            {isOpen && (
              <div
                ref={dropdownRef}
                className="absolute tablet:min-w-89 top-12 left-0 right-0 bg-neutral-object border border-neutral-quiet rounded-md shadow-lg py-2 z-50"
              >
                <div ref={scrollRef} className="max-h-55 overflow-auto ">
                  {(searchType === searchTypesEnum.user ||
                    searchType === searchTypesEnum.roles ||
                    searchType === searchTypesEnum.tags ||
                    searchType === searchTypesEnum.positions ||
                    searchType === searchTypesEnum.institutions) && (
                    <DropdownContent
                      isLoading={isLoading}
                      _data={_data}
                      searchType={searchType}
                      selected={selected}
                      handleSelectUser={handleSelectUser}
                      onUpdateSearch={onUpdateSearch}
                      setIsOpen={setIsOpen}
                      hasNextPage={hasNextPage}
                      loaderRef={loaderRef}
                    />
                  )}
                </div>
              </div>
            )}
            <div className="max-h-71 overflow-y-auto space-y-0.5 mt-6">
              {selected.length > 0 ? (
                <>
                  {searchType === searchTypesEnum.user &&
                    selected.map((user) => (
                      <UserCard
                        key={user.id}
                        user={user as UserType}
                        onUpdateSearch={onUpdateSearch}
                      />
                    ))}
                  {searchType === searchTypesEnum.roles &&
                    selected.map((role) => (
                      <RoleCard
                        key={role.id}
                        role={role as RoleType}
                        onUpdateSearch={onUpdateSearch}
                      />
                    ))}
                  {searchType === searchTypesEnum.tags &&
                    selected.map((tag) => (
                      <TagCard
                        key={tag.id}
                        tag={tag as TagType}
                        onUpdateSearch={onUpdateSearch}
                      />
                    ))}
                  {(searchType === searchTypesEnum.positions ||
                    searchType === searchTypesEnum.institutions) &&
                    selected.map((catalog) => (
                      <CatalogCard
                        key={catalog.id}
                        item={catalog as CatalogSearchItem}
                        onUpdateSearch={onUpdateSearch}
                      />
                    ))}
                </>
              ) : (
                <div className="w-full min-h-71 flex justify-center items-center text-on-disabled">
                  {isFetching ? (
                    <Icon
                      type="far"
                      name="spinner-third"
                      className="text-on-disabled animate-spin text-2xl"
                    />
                  ) : (
                    <span className="text-sm leading-2xl font-normal font-geist text-center">{`Agrega ${getListType(searchType)} a esta lista para activar el filtro`}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
